import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";

const TYPE_META = {
  compostelle: { label: "Saint-Jacques de Compostelle", couleur: "#d9a521", icon: "🐚" },
  rando:       { label: "Randonnée pédestre",           couleur: "#27ae60", icon: "🥾" },
  vtt:         { label: "VTT",                          couleur: "#e67e22", icon: "🚵" },
  autre:       { label: "Autre",                        couleur: "#7f8c8d", icon: "📍" },
};

const DIFFICULTE_META = {
  facile:     { label: "Facile",     color: "#27ae60" },
  moyenne:    { label: "Moyenne",    color: "#f39c12" },
  difficile:  { label: "Difficile",  color: "#e74c3c" },
};

function ensureLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  return new Promise((resolve, reject) => {
    if (!document.querySelector("link[data-leaflet]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.setAttribute("data-leaflet", "1");
      document.head.appendChild(link);
    }
    const existing = document.querySelector("script[data-leaflet]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.async = true;
    s.setAttribute("data-leaflet", "1");
    s.onload = () => resolve(window.L);
    s.onerror = () => reject(new Error("Échec du chargement de Leaflet"));
    document.body.appendChild(s);
  });
}

export default function CircuitsRandonnee() {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");

  const mapDiv = useRef(null);
  const leafletMap = useRef(null);
  const currentLayer = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/cartographie/traces")
      .then((r) => {
        if (cancelled) return;
        const items = (r.items || []).filter((t) => t.geojson);
        setTraces(items);
        if (items.length > 0) setSelectedId(items[0].id);
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger les circuits."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Initialisation de la carte UNE FOIS Leaflet chargé ET le conteneur monté.
  // On dépend de !loading pour s'assurer que le <div ref={mapDiv}> existe
  // (il n'est rendu dans le DOM qu'après le chargement des traces).
  useEffect(() => {
    if (loading || traces.length === 0) return;
    if (!mapDiv.current || leafletMap.current) return;
    let alive = true;
    ensureLeaflet().then((L) => {
      if (!alive || !mapDiv.current || leafletMap.current) return;
      const map = L.map(mapDiv.current, {
        center: [48.110035, 0.081686],
        zoom: 14,
        zoomControl: true,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      leafletMap.current = map;
      // Forcer le recalcul de la taille au cas où le conteneur n'avait pas
      // sa taille finale au moment de l'init
      setTimeout(() => {
        try { map.invalidateSize(); } catch (e) { /* nothing */ }
      }, 50);
      setMapReady(true);
    });
    return () => { alive = false; };
  }, [loading, traces.length]);

  // Cleanup carte au démontage
  useEffect(() => {
    return () => {
      if (leafletMap.current) {
        try { leafletMap.current.remove(); } catch (e) { /* nothing */ }
        leafletMap.current = null;
      }
    };
  }, []);

  // Rendu du tracé sélectionné — se redéclenche aussi quand la carte devient prête
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMap.current || !mapReady) return;
    if (currentLayer.current) {
      leafletMap.current.removeLayer(currentLayer.current);
      currentLayer.current = null;
    }
    if (!selectedId) return;
    const trace = traces.find((t) => t.id === selectedId);
    if (!trace || !trace.geojson) return;
    const meta = TYPE_META[trace.type] || TYPE_META.autre;
    const color = trace.couleur || meta.couleur;
    const layer = L.geoJSON(trace.geojson, {
      style: { color, weight: 5, opacity: 0.9 },
    });
    layer.addTo(leafletMap.current);
    currentLayer.current = layer;
    // Délai court avant fitBounds pour s'assurer que la taille est OK
    setTimeout(() => {
      if (!leafletMap.current || !currentLayer.current) return;
      try {
        leafletMap.current.invalidateSize();
        const b = currentLayer.current.getBounds();
        if (b.isValid()) leafletMap.current.fitBounds(b, { padding: [30, 30] });
      } catch (e) { /* nothing */ }
    }, 80);
  }, [selectedId, traces, mapReady]);

  const visibleTraces = typeFilter === "all"
    ? traces
    : traces.filter((t) => t.type === typeFilter);

  const selected = traces.find((t) => t.id === selectedId);

  return (
    <div className="container">
      <h1>Circuits de randonnée</h1>
      <p className="muted mb-3">
        Découvrez les sentiers et chemins de La Chapelle-Saint-Fray.
        Sélectionnez un circuit pour afficher son tracé sur la carte.
      </p>

      {loading && <p className="muted">Chargement des circuits…</p>}
      {error && <div className="error">{error}</div>}

      {!loading && traces.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "2rem 1rem" }}>
          <p className="muted" style={{ marginBottom: 0 }}>
            Aucun circuit n'est encore disponible. Revenez bientôt !
          </p>
        </div>
      )}

      {!loading && traces.length > 0 && (
        <>
          {/* Filtres par type */}
          <div className="rando-filters">
            <button
              className={`rando-filter-btn ${typeFilter === "all" ? "active" : ""}`}
              onClick={() => setTypeFilter("all")}>
              Tous ({traces.length})
            </button>
            {Object.entries(TYPE_META).map(([key, meta]) => {
              const count = traces.filter((t) => t.type === key).length;
              if (count === 0) return null;
              return (
                <button
                  key={key}
                  className={`rando-filter-btn ${typeFilter === key ? "active" : ""}`}
                  onClick={() => setTypeFilter(key)}
                  style={typeFilter === key ? {
                    borderColor: meta.couleur,
                    color: meta.couleur,
                  } : {}}>
                  <span style={{ marginRight: 4 }}>{meta.icon}</span>
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>

          <div className="rando-layout">
            {/* Liste des circuits */}
            <aside className="rando-list">
              <h3 style={{ marginTop: 0, fontSize: "1.05rem" }}>
                {visibleTraces.length} circuit{visibleTraces.length > 1 ? "s" : ""}
              </h3>
              {visibleTraces.length === 0 && (
                <p className="muted" style={{ fontSize: "0.9em" }}>
                  Aucun circuit dans cette catégorie.
                </p>
              )}
              <div className="rando-list-items">
                {visibleTraces.map((t) => {
                  const meta = TYPE_META[t.type] || TYPE_META.autre;
                  const isActive = t.id === selectedId;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      className={`rando-item ${isActive ? "active" : ""}`}
                      style={isActive ? { borderColor: t.couleur || meta.couleur } : {}}>
                      <div className="rando-item-head">
                        <span style={{ fontSize: "1.1em" }}>{meta.icon}</span>
                        <strong>{t.nom}</strong>
                      </div>
                      <div className="rando-item-meta">
                        {t.distance_km > 0 && (
                          <span>{t.distance_km.toFixed(1)} km</span>
                        )}
                        {t.difficulte && DIFFICULTE_META[t.difficulte] && (
                          <span style={{ color: DIFFICULTE_META[t.difficulte].color }}>
                            ● {DIFFICULTE_META[t.difficulte].label}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Carte + détails */}
            <div className="rando-map-wrap">
              <div ref={mapDiv} className="rando-map" />

              {selected && (
                <div className="rando-details card">
                  <h3 style={{ marginTop: 0 }}>
                    {(TYPE_META[selected.type] || TYPE_META.autre).icon} {selected.nom}
                  </h3>
                  <div className="rando-details-stats">
                    {selected.distance_km > 0 && (
                      <div className="rando-stat">
                        <span className="rando-stat-label">Distance</span>
                        <strong>{selected.distance_km.toFixed(1)} km</strong>
                      </div>
                    )}
                    {selected.difficulte && DIFFICULTE_META[selected.difficulte] && (
                      <div className="rando-stat">
                        <span className="rando-stat-label">Difficulté</span>
                        <strong style={{ color: DIFFICULTE_META[selected.difficulte].color }}>
                          {DIFFICULTE_META[selected.difficulte].label}
                        </strong>
                      </div>
                    )}
                    <div className="rando-stat">
                      <span className="rando-stat-label">Type</span>
                      <strong>{(TYPE_META[selected.type] || TYPE_META.autre).label}</strong>
                    </div>
                  </div>
                  {selected.description && (
                    <div className="rando-description">
                      {selected.description.split("\n").map((line, i) => (
                        <p key={i} style={{ margin: "0 0 0.5rem" }}>{line}</p>
                      ))}
                    </div>
                  )}
                  <div className="muted" style={{ fontSize: "0.8em", marginTop: "0.6rem" }}>
                    💡 Pensez à vérifier <Link to="/cartographie">l'état des voiries</Link> avant
                    de partir, surtout en période de pluie.
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
